/**
 * @file useHintPopover.ts
 * @brief Open state for one page's tips bubble (HintPopover.tsx).
 *
 * @details
 * Opens on its own until the person closes it once, then only from the icon,
 * as on the phone. Kept per user in localStorage; storage that throws means
 * it never pops up uninvited.
 *
 * @author DES
 * @date 05-10-2026
 */

import { useCallback, useRef, useState } from "react";
import { useAuth } from "@/shared/auth/auth-context";

/** Open state for one screen's tips; opens itself until closed once. */
export function useHintPopover(id: string) {
  const { user } = useAuth();
  const key = `verblizr.hint.${id}.closed.${user?.id ?? ""}`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) !== "1";
    } catch {
      return false; // storage blocked: never pop up uninvited
    }
  });
  const anchorRef = useRef<HTMLButtonElement>(null);

  const remember = useCallback(() => {
    try {
      localStorage.setItem(key, "1");
    } catch {
      /* storage blocked: it may open again next visit */
    }
  }, [key]);

  const close = useCallback(() => {
    setOpen(false);
    remember();
  }, [remember]);

  const toggle = useCallback(() => {
    setOpen((was) => {
      if (was) remember();
      return !was;
    });
  }, [remember]);

  return { open, toggle, close, anchorRef };
}
